import { API_BASE_URL } from '@/config/env.js'
import { apiFetch, updateMyProfile } from '@/lib/auth.js'
import { checkoutAttribution, validConversionSource } from './planNavigation.js'

export { updateMyProfile }

async function billingRead(fetcher, ...args) {
  try {
    return await fetcher(...args)
  } catch (error) {
    if (!(error instanceof TypeError)) throw error
    const failure = new Error('Não foi possível conectar. Verifique sua conexão e tente novamente.', { cause: error })
    failure.code = 'transport_error'
    throw failure
  }
}

async function readJson(res) {
  const data = await res.json().catch(() => ({}))
  if (!res.ok) {
    const message = data?.detail || data?.code || 'Erro ao acessar cobrança'
    const error = new Error(message)
    error.status = res.status
    error.data = data
    throw error
  }
  return data
}

export async function fetchPlans({ coupon, product } = {}) {
  const params = new URLSearchParams({ ...(coupon ? { coupon } : {}), ...(product ? { product } : {}) })
  const qs = params.size ? `?${params}` : ''
  const res = await billingRead(fetch, `${API_BASE_URL}/billing/plans/${qs}`)
  return readJson(res)
}

export async function previewCoupon({ code, planUuid, billingCycle = 'monthly' }) {
  const res = await apiFetch(`${API_BASE_URL}/billing/coupons/preview/`, {
    method: 'POST',
    body: JSON.stringify({ code, plan_uuid: planUuid, billing_cycle: billingCycle }),
  })
  return readJson(res)
}

export async function fetchCurrentLegalDocs() {
  const res = await apiFetch(`${API_BASE_URL}/accounts/legal/current/`)
  return readJson(res)
}

export async function acceptCurrentLegalDocs({ source = 'checkout' } = {}) {
  const res = await apiFetch(`${API_BASE_URL}/accounts/legal/accept/`, {
    method: 'POST',
    body: JSON.stringify({ source }),
  })
  return readJson(res)
}

function attributionPayload(value) {
  const safe = checkoutAttribution(value)
  return { ...(validConversionSource(value.conversionSource) !== 'direct' ? { conversion_source: value.conversionSource } : {}),
    ...(safe.offerId ? { offer_id: safe.offerId } : {}), ...(safe.policyVersion ? { policy_version: safe.policyVersion } : {}),
    ...(safe.variant ? { variant: safe.variant } : {}), ...(safe.copyVersion ? { copy_version: safe.copyVersion } : {}) }
}

export async function createEmbeddedCheckout({ planUuid, couponCode = '', billingCycle = 'monthly', requestId, quoteToken, ...metadata }) {
  const res = await apiFetch(`${API_BASE_URL}/billing/checkout/embedded/`, {
    method: 'POST',
    body: JSON.stringify({ plan_uuid: planUuid, coupon_code: couponCode, billing_cycle: billingCycle, request_id: requestId, quote_token: quoteToken, ...attributionPayload(metadata) }),
  })
  return readJson(res)
}

export async function createOpenPixCheckout({ planUuid, couponCode = '', billingCycle = 'monthly', requestId, quoteToken, ...metadata }) {
  const res = await apiFetch(`${API_BASE_URL}/billing/checkout/openpix/`, {
    method: 'POST',
    body: JSON.stringify({ plan_uuid: planUuid, coupon_code: couponCode, billing_cycle: billingCycle, request_id: requestId, quote_token: quoteToken, ...attributionPayload(metadata) }),
  })
  return readJson(res)
}

export async function previewCheckout({ planUuid, billingCycle = 'monthly', provider = 'stripe', couponCode = '' }) {
  return billingAction('checkout/preview/', { plan_uuid: planUuid, billing_cycle: billingCycle, provider, coupon_code: couponCode })
}

export async function previewPlanChange({ planUuid, billingCycle, provider }) {
  return billingAction('plan-change/preview/', { plan_uuid: planUuid, billing_cycle: billingCycle, provider })
}

export async function executePlanChange({ planUuid, billingCycle, provider, requestId, quoteToken }) {
  return billingAction('plan-change/', { plan_uuid: planUuid, billing_cycle: billingCycle, provider, request_id: requestId, quote_token: quoteToken })
}

export async function cancelBillingOperation(operationUuid) {
  return billingAction('plan-change/cancel/', { operation_uuid: operationUuid })
}

async function billingAction(path, payload) {
  const res = await apiFetch(`${API_BASE_URL}/billing/${path}`, { method: 'POST', body: JSON.stringify(payload) })
  return readJson(res)
}

export async function fetchCheckoutStatus(subscriptionUuid) {
  const res = await apiFetch(`${API_BASE_URL}/billing/checkout/status/${subscriptionUuid}/`)
  return readJson(res)
}

export async function fetchMySubscription({ product, periodsPage } = {}) {
  const params = new URLSearchParams({ ...(product ? { product } : {}), ...(periodsPage ? { periods_page: periodsPage } : {}) })
  const res = await billingRead(apiFetch, `${API_BASE_URL}/billing/subscriptions/me/${params.size ? `?${params}` : ''}`)
  return readJson(res)
}

export async function cancelMySubscription() {
  const res = await apiFetch(`${API_BASE_URL}/billing/subscriptions/me/cancel/`, {
    method: 'POST',
    body: JSON.stringify({}),
  })
  return readJson(res)
}

export async function changeMySubscriptionPlan(planUuid) {
  const res = await apiFetch(`${API_BASE_URL}/billing/subscriptions/me/change-plan/`, {
    method: 'POST',
    body: JSON.stringify({ plan_uuid: planUuid }),
  })
  return readJson(res)
}

export async function fetchCourses() {
  const res = await fetch(`${API_BASE_URL}/billing/courses/`)
  return readJson(res)
}

export async function fetchCourseDetail(slug) {
  const res = await fetch(`${API_BASE_URL}/billing/courses/${slug}/`)
  return readJson(res)
}

export async function createCourseCheckout(slug, payload = {}) {
  const res = await apiFetch(`${API_BASE_URL}/billing/courses/${slug}/checkout/`, {
    method: 'POST',
    body: JSON.stringify(payload),
  })
  return readJson(res)
}

export async function fetchMyCourses() {
  const res = await apiFetch(`${API_BASE_URL}/billing/courses/me/`)
  return readJson(res)
}

export async function fetchCourseJourney(slug) {
  const res = await apiFetch(`${API_BASE_URL}/billing/courses/${slug}/journey/`)
  return readJson(res)
}

export async function submitCourseExercise(slug, payload) {
  const res = await apiFetch(`${API_BASE_URL}/billing/courses/${slug}/exercise/`, {
    method: 'POST',
    body: JSON.stringify(payload),
  })
  return readJson(res)
}

export async function fetchAdminCourses() {
  const res = await apiFetch(`${API_BASE_URL}/billing/backoffice/courses/`)
  return readJson(res)
}

export async function createAdminCourse(payload) {
  const res = await apiFetch(`${API_BASE_URL}/billing/backoffice/courses/`, {
    method: 'POST',
    body: JSON.stringify(payload),
  })
  return readJson(res)
}

export async function updateAdminCourse(courseUuid, payload) {
  const res = await apiFetch(`${API_BASE_URL}/billing/backoffice/courses/${courseUuid}/`, {
    method: 'PATCH',
    body: JSON.stringify(payload),
  })
  return readJson(res)
}

export async function archiveAdminCourse(courseUuid) {
  const res = await apiFetch(`${API_BASE_URL}/billing/backoffice/courses/${courseUuid}/`, {
    method: 'DELETE',
    body: JSON.stringify({}),
  })
  if (!res.ok) return readJson(res)
  return true
}

// --- Backoffice: Plans ---

export async function fetchBackofficePlans(params = {}) {
  const qs = new URLSearchParams(params).toString()
  const url = qs ? `${API_BASE_URL}/billing/backoffice/plans/?${qs}` : `${API_BASE_URL}/billing/backoffice/plans/`
  const res = await apiFetch(url)
  return readJson(res)
}

export async function createBackofficePlan(payload) {
  const res = await apiFetch(`${API_BASE_URL}/billing/backoffice/plans/`, {
    method: 'POST',
    body: JSON.stringify(payload),
  })
  return readJson(res)
}

export async function updateBackofficePlan(planUuid, payload) {
  const res = await apiFetch(`${API_BASE_URL}/billing/backoffice/plans/${planUuid}/`, {
    method: 'PATCH',
    body: JSON.stringify(payload),
  })
  return readJson(res)
}

export async function retryBackofficePlanStripeSync(planUuid) {
  return readJson(await apiFetch(`${API_BASE_URL}/billing/backoffice/plans/${encodeURIComponent(planUuid)}/sync-stripe/`, { method: 'POST' }))
}

export async function deleteBackofficePlan(planUuid) {
  const res = await apiFetch(`${API_BASE_URL}/billing/backoffice/plans/${planUuid}/`, {
    method: 'DELETE',
  })
  if (!res.ok) return readJson(res)
  return true
}

// --- Backoffice: Features ---

export async function fetchBackofficeFeatures(params = {}) {
  const qs = new URLSearchParams(params).toString()
  const url = qs ? `${API_BASE_URL}/billing/backoffice/features/?${qs}` : `${API_BASE_URL}/billing/backoffice/features/`
  const res = await apiFetch(url)
  return readJson(res)
}

export async function createBackofficeFeature(payload) {
  const res = await apiFetch(`${API_BASE_URL}/billing/backoffice/features/`, {
    method: 'POST',
    body: JSON.stringify(payload),
  })
  return readJson(res)
}

export async function updateBackofficeFeature(featureUuid, payload) {
  const res = await apiFetch(`${API_BASE_URL}/billing/backoffice/features/${featureUuid}/`, {
    method: 'PATCH',
    body: JSON.stringify(payload),
  })
  return readJson(res)
}

export async function deleteBackofficeFeature(featureUuid) {
  const res = await apiFetch(`${API_BASE_URL}/billing/backoffice/features/${featureUuid}/`, {
    method: 'DELETE',
  })
  if (!res.ok) return readJson(res)
  return true
}

// --- Backoffice: Invoices ---

export async function fetchBackofficeInvoices(params = {}) {
  const qs = new URLSearchParams(params).toString()
  const url = qs
    ? `${API_BASE_URL}/billing/backoffice/invoices/?${qs}`
    : `${API_BASE_URL}/billing/backoffice/invoices/`
  const res = await apiFetch(url)
  return readJson(res)
}

// --- Backoffice: Subscriptions ---

export async function fetchBackofficeSubscription(subscriptionUuid) {
  const res = await apiFetch(`${API_BASE_URL}/billing/backoffice/subscriptions/${subscriptionUuid}/`)
  return readJson(res)
}

export async function updateBackofficeSubscription(subscriptionUuid, payload) {
  const res = await apiFetch(`${API_BASE_URL}/billing/backoffice/subscriptions/${subscriptionUuid}/`, {
    method: 'PATCH',
    body: JSON.stringify(payload),
  })
  return readJson(res)
}

// --- Backoffice: Coupons ---

export async function fetchBackofficeCoupons(params = {}) {
  const qs = new URLSearchParams(params).toString()
  const url = qs ? `${API_BASE_URL}/billing/backoffice/coupons/?${qs}` : `${API_BASE_URL}/billing/backoffice/coupons/`
  const res = await apiFetch(url)
  return readJson(res)
}

export async function createBackofficeCoupon(payload) {
  const res = await apiFetch(`${API_BASE_URL}/billing/backoffice/coupons/`, {
    method: 'POST',
    body: JSON.stringify(payload),
  })
  return readJson(res)
}

export async function updateBackofficeCoupon(couponUuid, payload) {
  const res = await apiFetch(`${API_BASE_URL}/billing/backoffice/coupons/${couponUuid}/`, {
    method: 'PATCH',
    body: JSON.stringify(payload),
  })
  return readJson(res)
}

export async function deleteBackofficeCoupon(couponUuid) {
  const res = await apiFetch(`${API_BASE_URL}/billing/backoffice/coupons/${couponUuid}/`, {
    method: 'DELETE',
  })
  if (!res.ok) return readJson(res)
  return true
}
